/**
 * @fileoverview Audit log detail modal with field-level change table and full document diff.
 * @module ui-admin/components/AuditLogDetailPanel
 *
 * Responsibilities:
 * - Render a modal showing a single AuditLogEntry in detail.
 * - Display field-level changes as a formatted before → after table.
 * - Display the full document before/after JSON diff for developer inspection.
 *
 * Dependencies:
 * - AuditLogTimeline types (AuditLogEntry, ChangeDetail)
 * - formatAuditValue utility for human-readable change value rendering
 * - @firesquirrel/audit formatAuditFieldLabel
 *
 * Architecture:
 * - Presentation-only component; receives an AuditLogEntry prop and renders it.
 * - No data fetching, no Dexie access, no API calls.
 *
 * Notes:
 * - The "Full Document Diff" section intentionally renders raw JSON.stringify
 *   output in a <pre> block for developer inspection. The field-level table
 *   uses formatAuditValue for human-readable display.
 */
import { useState } from 'react';
import { ChevronDown, ChevronUp, Code2 } from 'lucide-react';
import type { AuditLogEntry, ChangeDetail } from './AuditLogTimeline';
import { formatAuditValueFull } from '../utils/formatAuditValue';
import { expandAuditChanges } from '../utils/expandAuditChanges';
import { formatAuditFieldLabel, shouldHideAuditField } from '../../../audit/src/summaries';

interface AuditLogDetailPanelProps {
  entry: AuditLogEntry;
  onClose: () => void;
}

/**
 * @component AuditLogDetailPanel
 *
 * Purpose:
 * - Show a full audit log entry in a modal overlay with structured change information.
 *
 * Props:
 * - entry: The AuditLogEntry to display in detail.
 * - onClose: Callback to dismiss the modal.
 *
 * Behavior:
 * - Renders summary card, field-level change table (collapsible), and full
 *   JSON diff (collapsible, off by default to save memory on mobile devices).
 *
 * Data Source:
 * - local (prop-driven, no fetching)
 *
 * Constraints:
 * - Must not fetch data or call APIs.
 *
 * Performance:
 * - Full JSON diff is lazy-rendered (collapsed by default) to avoid rendering
 *   large <pre> blocks on mobile PWA devices with limited memory.
 */
export default function AuditLogDetailPanel({ entry, onClose }: AuditLogDetailPanelProps) {
  const [showFieldChanges, setShowFieldChanges] = useState(true);
  const [showFullDiff, setShowFullDiff] = useState(false);

  const displayChanges = expandAuditChanges(entry.changes).filter((ch) => !shouldHideAuditField(ch.field));
  const hasChanges = displayChanges.length > 0;
  const hasBeforeAfter = entry.before !== undefined || entry.after !== undefined;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Audit Log Detail</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Summary Card */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-500">Action:</span>
              <span
                className={`text-xs font-semibold px-2 py-1 rounded-full ${
                  entry.action === 'create'
                    ? 'bg-green-100 text-green-700'
                    : entry.action === 'update'
                      ? 'bg-blue-100 text-blue-700'
                      : entry.action === 'delete'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-gray-100 text-gray-600'
                }`}
              >
                {entry.action.toUpperCase()}
              </span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">Summary:</span>
              <p className="text-sm text-gray-900 mt-1">{entry.summary}</p>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-200">
              <div>
                <span className="text-xs font-medium text-gray-500">Performed By</span>
                <p className="text-sm text-gray-900">{entry.performedBy.name || 'Unknown User'}</p>
                <p className="text-xs text-gray-500">{entry.performedBy.email || 'No email'}</p>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500">When</span>
                <p className="text-sm text-gray-900">
                  {new Date(entry.createdAt).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </p>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500">Source</span>
                <p className="text-sm text-gray-900">{entry.source.app || 'Unknown'}</p>
                <p className="text-xs text-gray-500">{entry.source.deviceType || 'unknown'}</p>
              </div>
              {(entry.source as { ipAddress?: string }).ipAddress && (
                <div>
                  <span className="text-xs font-medium text-gray-500">IP Address</span>
                  <p className="text-sm text-gray-900 font-mono">{(entry.source as { ipAddress?: string }).ipAddress}</p>
                </div>
              )}
            </div>
            {entry.metadata?.reason && (
              <div className="pt-2 border-t border-gray-200">
                <span className="text-xs font-medium text-gray-500">Reason</span>
                <p className="text-sm text-gray-700 italic mt-1">{entry.metadata.reason}</p>
              </div>
            )}
          </div>

          {/* Field Changes Table */}
          {hasChanges && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setShowFieldChanges(!showFieldChanges)}
                className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900">Field Changes</span>
                  <span className="text-xs font-medium text-gray-500 bg-white px-2 py-0.5 rounded-full">
                    {displayChanges.length} {displayChanges.length === 1 ? 'field' : 'fields'}
                  </span>
                </div>
                {showFieldChanges ? (
                  <ChevronUp className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                )}
              </button>
              {showFieldChanges && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Field</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Before</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">After</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {displayChanges.map((change, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{formatAuditFieldLabel(change.field)}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className="text-red-600 line-through whitespace-pre-wrap wrap-break-word">{formatAuditValueFull(change.oldValue)}</span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className="text-green-600 font-medium whitespace-pre-wrap wrap-break-word">{formatAuditValueFull(change.newValue)}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-0.5 rounded-sm">
                              {change.fieldType}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Full Document Diff */}
          {hasBeforeAfter && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setShowFullDiff(!showFullDiff)}
                className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <Code2 className="h-4 w-4 text-gray-600" />
                  <span className="text-sm font-semibold text-gray-900">Full Document Diff</span>
                </div>
                {showFullDiff ? (
                  <ChevronUp className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                )}
              </button>
              {showFullDiff && (
                <div className="grid grid-cols-2 divide-x divide-gray-200">
                  {/* Before */}
                  <div className="p-4">
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-2 flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-red-500" />
                      Before
                    </div>
                    <pre className="text-xs font-mono text-gray-700 bg-gray-50 p-3 rounded-sm overflow-x-auto max-h-96 overflow-y-auto">
                      {entry.before ? JSON.stringify(entry.before, null, 2) : '(empty)'}
                    </pre>
                  </div>
                  {/* After */}
                  <div className="p-4">
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-2 flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-green-500" />
                      After
                    </div>
                    <pre className="text-xs font-mono text-gray-700 bg-gray-50 p-3 rounded-sm overflow-x-auto max-h-96 overflow-y-auto">
                      {entry.after ? JSON.stringify(entry.after, null, 2) : '(empty)'}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* No Changes Message */}
          {!hasChanges && !hasBeforeAfter && (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm">No detailed change information available for this audit entry.</p>
              <p className="text-xs mt-1">This may be a legacy audit entry or a non-modifying action.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors text-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

